import {
  Beer, Car, CarFront, Coffee, Cpu, Droplets, Dumbbell, Flame, Flower2, Hammer, Hand, type LucideIcon, Pill, Printer, Sandwich, Scissors, Shirt,
  ShoppingBasket, Smartphone, Sparkles, SprayCan, Store, Truck, UtensilsCrossed, WashingMachine, Wrench,
} from 'lucide-react';
import clsx from 'clsx';
import { BUSINESS_TYPES, businessType, FAMILIES, type FamilyKey, MODULES, type ModuleKey, modulesFromProfile, resolveModules } from '@makila/pos-core';
import { Badge, Field, Input, Toggle } from '../ui';

export const FAMILY_ICON: Record<FamilyKey, LucideIcon> = {
  comercio: Store, alimentacao: UtensilsCrossed, beleza: Sparkles, distribuicao: Truck, moda: Shirt, servicos: Wrench,
};
export const TYPE_ICON: Record<string, LucideIcon> = {
  retail: ShoppingBasket, store: Store, hardware: Hammer, stationery: Printer, electronics: Smartphone, cosmetics: SprayCan, pharmacy: Pill,
  restaurant: UtensilsCrossed, cafe: Coffee, fastfood: Sandwich, bar: Beer,
  barber: Scissors, salon: Sparkles, spa: Flower2, nails: Hand, gym: Dumbbell,
  water: Droplets, gas: Flame, fashion: Shirt, laundry: WashingMachine, garage: Car, repair: Cpu, carwash: CarFront,
};

export interface BusinessChoice { type: string | null; answers: Record<string, string | boolean | number>; modules: Partial<Record<ModuleKey, boolean>> }
export const emptyChoice = (): BusinessChoice => ({ type: null, answers: {}, modules: {} });

/** Módulos que a escolha liga (para o resumo): tipo + respostas + interruptores. */
export function choiceModules(c: BusinessChoice): Partial<Record<ModuleKey, boolean>> {
  if (!c.type) return {};
  return { ...modulesFromProfile(businessType(c.type), c.answers), ...c.modules };
}

/**
 * Passo "Tipo de negócio" (docs/14 §3): família → tipo → 2 a 4 perguntas →
 * resumo dos módulos, com interruptores que se podem mudar já.
 */
export function BusinessPicker({ value, onChange }: { value: BusinessChoice; onChange: (c: BusinessChoice) => void }) {
  const type = value.type ? businessType(value.type) : null;
  const family = type?.family ?? (value.answers.__family as FamilyKey | undefined) ?? null;
  const overrides = choiceModules(value);
  // O que fica activo no teste (14 dias com tudo); os "em breve" mostram-se mas não se ligam.
  const active = type ? resolveModules(type.code, overrides, 'trial') : [];
  const relevant = type ? MODULES.filter((m) => type.modules.includes(m.key) || overrides[m.key] !== undefined || active.includes(m.key)) : [];

  return (
    <div className="space-y-8">
      <div>
        <h3 className="mb-3 text-sm font-semibold">Que tipo de negócio tem?</h3>
        <div role="radiogroup" aria-label="Família de negócio" className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {FAMILIES.map((f) => {
            const Icon = FAMILY_ICON[f.key];
            const on = family === f.key;
            return (
              <button key={f.key} type="button" role="radio" aria-checked={on}
                onClick={() => onChange({ type: null, answers: { __family: f.key }, modules: {} })}
                className={clsx('flex min-h-24 flex-col items-start gap-2 rounded-2xl border-2 p-3.5 text-left transition', on ? 'border-brand-700 bg-brand-50/60 dark:bg-white/5' : 'border-base hover:border-brand-500/50')}>
                <Icon size={24} className={on ? 'text-brand-700' : 'text-muted'} />
                <span className="font-semibold leading-tight">{f.name}</span>
                <span className="text-xs leading-snug text-muted">{f.description}</span>
              </button>
            );
          })}
        </div>
      </div>

      {family && (
        <div>
          <h3 className="mb-3 text-sm font-semibold">Qual destes descreve melhor o seu negócio?</h3>
          <div role="radiogroup" aria-label="Tipo de negócio" className="grid gap-2 sm:grid-cols-2">
            {BUSINESS_TYPES.filter((t) => t.family === family).map((t) => {
              const Icon = TYPE_ICON[t.code] ?? Store;
              const on = type?.code === t.code;
              return (
                <button key={t.code} type="button" role="radio" aria-checked={on} onClick={() => onChange({ type: t.code, answers: {}, modules: {} })}
                  className={clsx('flex min-h-[72px] items-center gap-3 rounded-xl border-2 p-3 text-left transition', on ? 'border-brand-700 bg-brand-50/60 dark:bg-white/5' : 'border-base hover:border-brand-500/50')}>
                  <span className={clsx('grid h-11 w-11 shrink-0 place-items-center rounded-xl', on ? 'bg-brand-700 text-white' : 'bg-surface-2 text-muted')}><Icon size={22} /></span>
                  <span className="min-w-0"><span className="block font-semibold leading-tight">{t.name}</span><span className="mt-0.5 line-clamp-2 block text-xs leading-snug text-muted">{t.description}</span></span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {type && type.questions.length > 0 && (
        <div>
          <h3 className="mb-3 text-sm font-semibold">Mais umas perguntas, para preparar o sistema</h3>
          <div className="space-y-3">
            {type.questions.map((q) => {
              const v = value.answers[q.key] ?? q.default;
              const set = (x: string | boolean | number) => onChange({ ...value, answers: { ...value.answers, [q.key]: x } });
              if (q.kind === 'number') {
                return <Field key={q.key} label={q.label} hint={q.hint}><Input type="number" min={0} max={200} className="w-30" value={String(v ?? '')} onChange={(e) => set(Math.max(0, Math.min(200, Number(e.target.value) || 0)))} /></Field>;
              }
              const opts = q.kind === 'yesno' ? [{ value: 'true', label: 'Sim' }, { value: 'false', label: 'Não' }] : q.options!.map((o) => ({ value: o.value, label: o.label }));
              return (
                <fieldset key={q.key} className="rounded-xl border border-base p-3.5">
                  <legend className="sr-only">{q.label}</legend>
                  <div className="mb-2.5 text-sm font-medium">{q.label}</div>
                  <div className="flex flex-wrap gap-2">
                    {opts.map((o) => {
                      const on = String(v) === o.value;
                      return (
                        <button key={o.value} type="button" aria-pressed={on} onClick={() => set(q.kind === 'yesno' ? o.value === 'true' : o.value)}
                          className={clsx('h-10 rounded-full border px-4 text-sm font-medium transition', on ? 'border-brand-700 bg-brand-700 text-white' : 'border-base hover:bg-surface-2')}>{o.label}</button>
                      );
                    })}
                  </div>
                </fieldset>
              );
            })}
          </div>
        </div>
      )}

      {type && (
        <div>
          <h3 className="text-sm font-semibold">Vamos activar</h3>
          <p className="mb-3 text-xs text-muted">Pode mudar já, ou mais tarde em Definições → Módulos. Nada se apaga ao desligar.</p>
          <div className="grid gap-2 sm:grid-cols-2">
            {relevant.map((m) => (
              <Toggle key={m.key} label={m.name} description={m.description} disabled={m.soon}
                badge={m.soon ? <Badge tone="info">Em breve</Badge> : m.minPlan !== 'caixa' ? <Badge>Plano {m.minPlan === 'loja' ? 'Loja' : 'Rede'}</Badge> : undefined}
                checked={!m.soon && active.includes(m.key)}
                onChange={(on) => onChange({ ...value, modules: { ...value.modules, [m.key]: on } })} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** O que enviar à API: o tipo, as respostas (sem as auxiliares) e os módulos mexidos à mão. */
export function choicePayload(c: BusinessChoice) {
  const { __family: _f, ...answers } = c.answers;
  return { businessType: c.type!, profile: answers, modules: c.modules };
}
